/**
 * bossSystem.ts
 * Lifecycle orchestrator for the World 2 boss (Void Mothership).
 *
 * Sibling to `spaceEnemySystem.ts`. Owns the lifecycle of
 * `spaceState.boss` — the boss is a singleton (only one alive at a time),
 * so the boss instance itself is not pooled; it is kept as a single
 * lazily-constructed object re-spawned via `spawn()`. Boss-fired
 * projectiles and drone spawning live in sibling modules
 * (`bossTurret.ts`, `bossDroneSpawner.ts`) — extracted to keep this
 * module within the 300-line file budget. Their public surfaces are
 * re-exported below so callers keep importing from `bossSystem`.
 *
 * Responsibilities:
 *  1. `triggerBoss()` — promote the boss into `spaceState.boss` once the
 *     scene's elapsed clock crosses `VOID_MOTHERSHIP_TRIGGER_TIME_S`.
 *  2. `updateBoss()` — tick the boss's own state machine, delegate to
 *     `fireBossTurret` / `spawnBossDrone` on cadence during HOLD, reap
 *     on death.
 *  3. `resolveShipProjectileBossCollisions()` — pairwise AABB tests
 *     between live ship projectiles and the boss.
 *  4. `resetBoss()` — drop the boss back to `null` and release all live
 *     boss projectiles (called by SpaceScene init / destroy).
 *
 * Boss-spawned fighter drones intentionally do NOT get their own
 * lifecycle list — once acquired from the shared fighter pool they
 * live on `spaceState.enemies` and inherit `spaceEnemySystem`'s update,
 * collision, and reset paths.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift (boss greybox).
 *
 * @module bossSystem
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';
import { VoidMothership } from '../entities/VoidMothership';
import { fireBossTurret, releaseAllBossProjectiles } from './bossTurret';
import { spawnBossDrone } from './bossDroneSpawner';
import { stepBossRam } from './bossRam';
import { fireBossMissile, releaseAllBossMissiles } from './bossMissileBarrage';
import { spawnExplosion } from './explosionVfx';
import {
  VOID_MOTHERSHIP_TRIGGER_TIME_S,
  VOID_MOTHERSHIP_TURRET_COOLDOWN_S,
  VOID_MOTHERSHIP_DRONE_SPAWN_COOLDOWN_S,
  VOID_MOTHERSHIP_MISSILE_COOLDOWN_S,
} from '../../constants/bossConstants';

// Re-export sibling helpers so callers keep importing from `bossSystem`.
export {
  fireBossTurret,
  updateBossProjectiles,
  releaseAllBossProjectiles,
  _bossProjectilePoolAvailable,
} from './bossTurret';
export {
  spawnBossDrone,
  setBossDroneRng,
  resetBossDroneRng,
} from './bossDroneSpawner';
export { stepBossRam } from './bossRam';
export {
  fireBossMissile,
  updateBossMissiles,
  releaseAllBossMissiles,
  _bossMissilePoolAvailable,
} from './bossMissileBarrage';

/** Reusable empty level for boss update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Lazily-constructed boss singleton. Held module-locally so re-init of a
 * space level reuses the same instance (cheap re-spawn rather than a
 * fresh GC allocation). Constructed on first `triggerBoss()` because the
 * test harness imports this module without ever spawning the boss.
 *
 * @internal
 */
let bossInstance: VoidMothership | null = null;

/**
 * One-shot guard: set to `true` the first time the boss is spawned in a
 * given space-level session, cleared by `resetBoss()`. Prevents the
 * trigger loop from re-spawning the boss every tick after it dies —
 * `spaceState.elapsedSeconds` stays past the trigger threshold once it
 * crosses, so without this flag a defeated boss would respawn on the
 * next fixed step.
 *
 * @internal
 */
let bossSpawnedThisLevel = false;

/**
 * Test-only helper: returns true when the module-level boss instance has
 * been constructed at least once. Lets tests assert the lazy-construction
 * behaviour without exposing the instance itself.
 *
 * @internal
 */
export function _hasBossInstance(): boolean {
  return bossInstance !== null;
}

/**
 * Test-only helper: returns the value of the one-shot spawn guard.
 *
 * @internal
 */
export function _hasBossSpawnedThisLevel(): boolean {
  return bossSpawnedThisLevel;
}

/**
 * Release the boss back to the inactive pool of one and clear
 * `spaceState.boss`. Called from `SpaceScene.init()` / `destroy()` so the
 * boss never bleeds across sessions.
 *
 * Like the other space-system resets this does NOT touch the elapsed
 * clock or RNG so a test-pinned state stays in place across resets.
 */
export function resetBoss(): void {
  if (bossInstance !== null) {
    bossInstance.dead = true;
    bossInstance.active = false;
    bossInstance.state = 'DEFEATED';
  }
  spaceState.boss = null;
  bossSpawnedThisLevel = false;
  releaseAllBossProjectiles();
  releaseAllBossMissiles();
}

/**
 * Promote the boss into the active scene once the elapsed clock crosses
 * `VOID_MOTHERSHIP_TRIGGER_TIME_S`. Idempotent: no-op if a boss is
 * already active, has already spawned this level (one-shot — defeated
 * bosses do NOT respawn), OR if the scene has not yet elapsed enough.
 *
 * Called once per fixed step from `spaceStep`.
 */
export function triggerBoss(): void {
  if (spaceState.boss !== null) return;
  if (bossSpawnedThisLevel) return;
  if (spaceState.elapsedSeconds < VOID_MOTHERSHIP_TRIGGER_TIME_S) return;

  if (bossInstance === null) {
    bossInstance = new VoidMothership();
  }
  bossInstance.spawn(spaceState.cameraX);
  spaceState.boss = bossInstance;
  bossSpawnedThisLevel = true;
}

/**
 * Advance the boss one fixed timestep and reap it on death.
 *
 * DECISION: Pool-miss-no-burn convention for the turret, drone, and
 * missile cadences — only reload the cooldown when the helper actually
 * fired. Mirrors `tryFireShipProjectile`. At greybox cadence the pools
 * can't realistically exhaust, but keeping the three sides consistent
 * avoids a future tuning footgun.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateBoss(dt: number): void {
  const boss = spaceState.boss;
  if (boss === null) return;
  boss.update(dt, EMPTY_SPACE_LEVEL);
  stepBossRam(boss, dt);
  if (!boss.dead && boss.state === 'HOLD' && boss.turretCooldown <= 0) {
    if (fireBossTurret()) {
      boss.turretCooldown = VOID_MOTHERSHIP_TURRET_COOLDOWN_S;
    }
  }
  if (!boss.dead && boss.state === 'HOLD' && boss.droneSpawnCooldown <= 0) {
    if (spawnBossDrone()) {
      boss.droneSpawnCooldown = VOID_MOTHERSHIP_DRONE_SPAWN_COOLDOWN_S;
    }
  }
  if (!boss.dead && boss.state === 'HOLD' && boss.missileCooldown <= 0) {
    if (fireBossMissile()) {
      boss.missileCooldown = VOID_MOTHERSHIP_MISSILE_COOLDOWN_S;
    }
  }
  if (boss.dead) {
    spaceState.boss = null;
  }
}

/**
 * Pairwise AABB collision between live ship projectiles and the boss.
 *
 * Each hit deals 1 damage and is consumed by the projectile (`dead =
 * true`, `active = false`); `updateShipProjectiles` owns the splice +
 * pool-release on the next tick. Boss death is observable through
 * `spaceState.boss === null` after the following `updateBoss()` call.
 *
 * Off-screen guard: skipped when the boss's LEFT edge is past the right
 * edge of the viewport (mirrors the visibility gate in
 * `resolveShipProjectileEnemyCollisions`) so projectiles can't connect
 * with a boss still finishing its ENTRY slide off-screen.
 */
export function resolveShipProjectileBossCollisions(): void {
  const boss = spaceState.boss;
  if (boss === null || boss.dead) return;

  const viewportRight = spaceState.cameraX + VIEWPORT_WIDTH;
  if (boss.x >= viewportRight) return; // Boss still off-screen.

  const projectiles = spaceState.projectiles;
  for (let i = 0; i < projectiles.length; i++) {
    const p = projectiles[i];
    if (p.dead) continue;
    const pRight = p.x + p.width;
    const pBottom = p.y + p.height;
    // Inlined AABB overlap to avoid getHitbox allocation in the hot path.
    if (
      pRight <= boss.x ||
      p.x >= boss.x + boss.width ||
      pBottom <= boss.y ||
      p.y >= boss.y + boss.height
    ) {
      continue;
    }
    boss.takeDamage(1, p.x + p.width / 2, p.y + p.height / 2);
    p.dead = true;
    p.active = false;
    if (boss.dead) {
      spawnBossDeathBursts(boss.x, boss.y, boss.width, boss.height);
      break; // No point processing further hits after death.
    }
  }
}

/**
 * Spawn the boss-kill VFX: a centre burst plus four corner-quadrant
 * bursts offset by 30% of the half-extents from the centre (which is
 * equivalent to 20% inset from each AABB edge). The 120 × 96 boss is
 * much larger than a single 32 × 32 explosion frame, so a single burst
 * at the centre reads as underwhelming for a capital-ship kill. Five
 * bursts fit inside the boss footprint and remain well under the
 * `EXPLOSION_POOL_SIZE` budget (16) so the next enemy kill that frame
 * still gets its own burst.
 *
 * @param x      - Boss top-left X in world space.
 * @param y      - Boss top-left Y in world space.
 * @param width  - Boss AABB width.
 * @param height - Boss AABB height.
 */
function spawnBossDeathBursts(
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const cx = x + width / 2;
  const cy = y + height / 2;
  // DECISION: Corner bursts sit at ±30% of the half-extent from the
  // centre (= 20% in from each edge). Keeps every burst inside the
  // boss footprint so the cluster reads as one big detonation, not
  // five stray pops, while still being visibly separated from the
  // centre burst.
  const ox = width * 0.3;
  const oy = height * 0.3;
  spawnExplosion(cx, cy);
  spawnExplosion(cx - ox, cy - oy);
  spawnExplosion(cx + ox, cy - oy);
  spawnExplosion(cx - ox, cy + oy);
  spawnExplosion(cx + ox, cy + oy);
}
